// frontend/src/widgets/CameraHeartbeatWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-20
// Updated:      2026-09-07 14:55 BST
//
// Description:
//   Camera heartbeat / device status widget.
//   Responsibilities:
//     - Display device online / stale / offline / disabled state
//     - Show last ingest time
//     - Show active job binding
//     - Provide quick operational visibility for camera health
//
// Mobile Responsiveness (2026-09-07):
//   - Preserves the existing desktop summary, refresh control, and heartbeat table.
//   - Reflows the four heartbeat summary tiles into a compact 2x2 mobile grid.
//   - Uses the existing table/row DOM as mobile device cards; no duplicate list render.
//   - Measures the first rendered camera card so one complete device record is
//     visible in the mobile list viewport before scrolling to the next record.
//   - Expands the nearest ReactGridLayout host to the widget's natural mobile
//     height because mobile widgets do not expose the desktop resize handle.
//   - Makes Refresh touch-friendly and prevents long identifiers from overflowing.
//   - Keeps the 30-second refresh cadence, heartbeat thresholds, sorting,
//     camera fetch, and existing per-camera binding lookup behaviour unchanged.
//
// Performance Note:
//   - The existing heartbeat refresh performs one camera-list request plus one
//     binding-history request per camera. This N+1 pattern is preserved here
//     intentionally so responsiveness work does not alter operational behaviour.
//     It should be reviewed separately as a backend/performance improvement.
//
// Notes:
//   - Uses ingest_last_used_at as the heartbeat source of truth.
//   - Uses existing camera binding endpoints to show current active job.
//   - Status thresholds:
//       * Online: last ingest within 2 minutes
//       * Stale:  > 2 minutes and <= 10 minutes
//       * Offline: > 10 minutes
//       * Disabled: ingest_enabled = false

import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const ONLINE_MINUTES = 2;
const STALE_MINUTES = 10;
const MOBILE_BREAKPOINT = 768;
const MOBILE_CARD_FALLBACK_HEIGHT = 420;

const formatDateTime = (value) => {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const minutesSince = (value) => {
  if (!value) return null;
  const diffMs = Date.now() - new Date(value).getTime();
  return Math.max(0, Math.floor(diffMs / 60000));
};

const getStatusMeta = (camera) => {
  if (!camera.ingest_enabled) {
    return { key: "disabled", label: "Disabled" };
  }

  const mins = minutesSince(camera.ingest_last_used_at);
  if (mins === null) {
    return { key: "offline", label: "Never Seen" };
  }
  if (mins <= ONLINE_MINUTES) {
    return { key: "online", label: "Online" };
  }
  if (mins <= STALE_MINUTES) {
    return { key: "stale", label: "Stale" };
  }
  return { key: "offline", label: "Offline" };
};

const statusChipStyle = (statusKey) => {
  switch (statusKey) {
    case "online":
      return {
        backgroundColor: "#ecfdf5",
        border: "1px solid #a7f3d0",
        color: "#065f46",
      };
    case "stale":
      return {
        backgroundColor: "#fffbeb",
        border: "1px solid #fde68a",
        color: "#92400e",
      };
    case "disabled":
      return {
        backgroundColor: "#f3f4f6",
        border: "1px solid #d1d5db",
        color: "#4b5563",
      };
    case "offline":
    default:
      return {
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#991b1b",
      };
  }
};

const mobileLabelStyle = {
  fontSize: "0.72rem",
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.035em",
  lineHeight: 1.25,
};

const CameraHeartbeatWidget = () => {
  const { theme } = useTheme();

  const [cameras, setCameras] = useState([]);
  const [bindingsByCamera, setBindingsByCamera] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [mobileCardViewportHeight, setMobileCardViewportHeight] = useState(null);

  const rootRef = useRef(null);
  const firstCameraRowRef = useRef(null);

  const loadData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError("");

    try {
      const camerasRes = await axiosInstance.get("anpr/cameras");
      const cameraRows = Array.isArray(camerasRes.data) ? camerasRes.data : [];
      setCameras(cameraRows);

      const bindingResults = await Promise.all(
        cameraRows.map(async (camera) => {
          try {
            const bindingRes = await axiosInstance.get(
              `anpr/cameras/${camera.id}/bindings`
            );
            const history = Array.isArray(bindingRes.data) ? bindingRes.data : [];
            const active =
              history.find((row) => row.is_active && !row.active_to) || null;
            return [camera.id, active];
          } catch (err) {
            console.error(
              `Failed to load bindings for camera ${camera.id}`,
              err
            );
            return [camera.id, null];
          }
        })
      );

      setBindingsByCamera(Object.fromEntries(bindingResults));
    } catch (err) {
      console.error("Failed to load camera heartbeat data", err);
      setError(
        err?.response?.data?.detail ||
          "Failed to load camera heartbeat data."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    const timer = window.setInterval(() => {
      loadData({ silent: true });
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  const sortedCameras = useMemo(() => {
    return [...cameras].sort((a, b) => {
      const aStatus = getStatusMeta(a).key;
      const bStatus = getStatusMeta(b).key;

      const priority = {
        offline: 0,
        stale: 1,
        online: 2,
        disabled: 3,
      };

      if (priority[aStatus] !== priority[bStatus]) {
        return priority[aStatus] - priority[bStatus];
      }

      const aName = (a.camera_name || "").toLowerCase();
      const bName = (b.camera_name || "").toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [cameras]);

  const summary = useMemo(() => {
    const counts = {
      online: 0,
      stale: 0,
      offline: 0,
      disabled: 0,
    };

    cameras.forEach((camera) => {
      const status = getStatusMeta(camera).key;
      counts[status] += 1;
    });

    return counts;
  }, [cameras]);

  useEffect(() => {
    if (!isMobile || sortedCameras.length === 0) {
      setMobileCardViewportHeight(null);
      return undefined;
    }

    let frameId = null;
    let observer = null;

    const measureFirstCameraCard = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const row = firstCameraRowRef.current;
        if (!row) return;

        const height = Math.ceil(row.getBoundingClientRect().height);
        if (height > 0) {
          setMobileCardViewportHeight(height);
        }
      });
    };

    measureFirstCameraCard();
    window.addEventListener("resize", measureFirstCameraCard);

    if (typeof ResizeObserver !== "undefined" && firstCameraRowRef.current) {
      observer = new ResizeObserver(measureFirstCameraCard);
      observer.observe(firstCameraRowRef.current);
    }

    return () => {
      window.removeEventListener("resize", measureFirstCameraCard);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isMobile, sortedCameras]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [
    isMobile,
    mobileCardViewportHeight,
    sortedCameras.length,
    error,
    refreshing,
  ]);

  if (loading) {
    return (
      <div style={{ padding: "8px 4px", color: "#444" }}>
        Loading device heartbeat...
      </div>
    );
  }

  const refreshButtonStyle = {
    backgroundColor: theme.button_action?.normal || "#000",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    fontWeight: 700,
    fontFamily: "var(--font-body)",
    cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
    ...(isMobile
      ? {
          width: "100%",
          minHeight: "44px",
          fontSize: "0.95rem",
        }
      : {}),
  };

  const mobileCellStyle = (isLast = false) =>
    isMobile
      ? {
          display: "grid",
          gridTemplateColumns: "96px minmax(0, 1fr)",
          gap: "10px",
          alignItems: "start",
          padding: "10px 12px",
          borderBottom: isLast ? "none" : "1px solid #efefef",
          minWidth: 0,
          whiteSpace: "normal",
        }
      : {
          padding: "10px 12px",
        };

  const mobileCardHeight =
    mobileCardViewportHeight || MOBILE_CARD_FALLBACK_HEIGHT;

  return (
    <div
      ref={rootRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        height: isMobile ? "auto" : "100%",
        minHeight: 0,
        width: "100%",
        overflow: isMobile ? "visible" : undefined,
      }}
    >
      {error && (
        <div
          style={{
            backgroundColor: "#fff4f4",
            border: "1px solid #f0caca",
            color: "#a10000",
            borderRadius: "8px",
            padding: "10px 12px",
            fontSize: "0.95rem",
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(4, minmax(0, 1fr))",
          gap: isMobile ? "8px" : "12px",
        }}
      >
        {[
          {
            label: "Online",
            value: summary.online,
            style: statusChipStyle("online"),
          },
          {
            label: "Stale",
            value: summary.stale,
            style: statusChipStyle("stale"),
          },
          {
            label: "Offline",
            value: summary.offline,
            style: statusChipStyle("offline"),
          },
          {
            label: "Disabled",
            value: summary.disabled,
            style: statusChipStyle("disabled"),
          },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              borderRadius: "10px",
              padding: isMobile ? "10px" : "12px",
              minWidth: 0,
              ...item.style,
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "0.82rem" : "0.9rem",
                fontWeight: 700,
              }}
            >
              {item.label}
            </div>
            <div
              style={{
                fontSize: isMobile ? "1.35rem" : "1.5rem",
                fontWeight: 800,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <button
          type="button"
          onClick={() => loadData({ silent: true })}
          disabled={refreshing}
          style={refreshButtonStyle}
          onMouseEnter={(e) => {
            if (!refreshing) {
              e.currentTarget.style.backgroundColor =
                theme.button_action?.hover || "#333";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              theme.button_action?.normal || "#000";
          }}
          onMouseDown={(e) => {
            if (!refreshing) {
              e.currentTarget.style.backgroundColor =
                theme.button_action?.click || "#111";
            }
          }}
          onMouseUp={(e) => {
            if (!refreshing) {
              e.currentTarget.style.backgroundColor =
                theme.button_action?.hover || "#333";
            }
          }}
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {sortedCameras.length === 0 ? (
        <div style={{ color: "#555", padding: "8px 4px" }}>
          No devices found for this tenant.
        </div>
      ) : (
        <div
          style={{
            overflowX: isMobile ? "hidden" : "auto",
            overflowY: isMobile ? "auto" : "visible",
            height: isMobile ? `${mobileCardHeight}px` : "auto",
            maxHeight: isMobile ? `${mobileCardHeight}px` : "none",
            minHeight: isMobile ? `${mobileCardHeight}px` : 0,
            border: "1px solid #ececec",
            borderRadius: "10px",
            backgroundColor: "#fff",
            scrollSnapType: isMobile ? "y mandatory" : "none",
            overscrollBehavior: isMobile ? "contain" : "auto",
            WebkitOverflowScrolling: isMobile ? "touch" : undefined,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: isMobile ? "separate" : "collapse",
              borderSpacing: 0,
              fontSize: "0.92rem",
              display: isMobile ? "block" : "table",
            }}
          >
            <thead style={{ display: isMobile ? "none" : "table-header-group" }}>
              <tr style={{ backgroundColor: "#f7f7f7", textAlign: "left" }}>
                <th style={{ padding: "10px 12px" }}>Device</th>
                <th style={{ padding: "10px 12px" }}>Status</th>
                <th style={{ padding: "10px 12px" }}>Last Seen</th>
                <th style={{ padding: "10px 12px" }}>Age</th>
                <th style={{ padding: "10px 12px" }}>Active Job</th>
                <th style={{ padding: "10px 12px" }}>Serial</th>
                <th style={{ padding: "10px 12px" }}>MAC</th>
              </tr>
            </thead>

            <tbody style={{ display: isMobile ? "block" : "table-row-group" }}>
              {sortedCameras.map((camera, index) => {
                const status = getStatusMeta(camera);
                const activeBinding = bindingsByCamera[camera.id];
                const ageMins = minutesSince(camera.ingest_last_used_at);

                return (
                  <tr
                    key={camera.id}
                    ref={index === 0 ? firstCameraRowRef : null}
                    style={{
                      borderTop: isMobile ? "none" : "1px solid #efefef",
                      display: isMobile ? "block" : "table-row",
                      width: isMobile ? "100%" : undefined,
                      minWidth: 0,
                      backgroundColor: "#fff",
                      scrollSnapAlign: isMobile ? "start" : "none",
                      scrollSnapStop: isMobile ? "always" : "normal",
                    }}
                  >
                    <td style={mobileCellStyle()}>
                      {isMobile && (
                        <div style={mobileLabelStyle}>Device</div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            color: theme.accent_color || "#000",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {camera.camera_name || `Camera ${camera.id}`}
                        </div>
                        <div
                          style={{
                            color: "#666",
                            fontSize: "0.85rem",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {camera.model_no || "Unknown model"}
                        </div>
                      </div>
                    </td>

                    <td style={mobileCellStyle()}>
                      {isMobile && (
                        <div style={mobileLabelStyle}>Status</div>
                      )}
                      <div>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            ...statusChipStyle(status.key),
                          }}
                        >
                          {status.label}
                        </span>
                      </div>
                    </td>

                    <td style={mobileCellStyle()}>
                      {isMobile && (
                        <div style={mobileLabelStyle}>Last Seen</div>
                      )}
                      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {formatDateTime(camera.ingest_last_used_at)}
                      </div>
                    </td>

                    <td style={mobileCellStyle()}>
                      {isMobile && <div style={mobileLabelStyle}>Age</div>}
                      <div>
                        {ageMins === null ? "—" : `${ageMins} min`}
                      </div>
                    </td>

                    <td style={mobileCellStyle()}>
                      {isMobile && (
                        <div style={mobileLabelStyle}>Active Job</div>
                      )}
                      <div>
                        {activeBinding ? `#${activeBinding.job_id}` : "—"}
                      </div>
                    </td>

                    <td style={mobileCellStyle()}>
                      {isMobile && <div style={mobileLabelStyle}>Serial</div>}
                      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {camera.serial_no || "—"}
                      </div>
                    </td>

                    <td style={mobileCellStyle(true)}>
                      {isMobile && <div style={mobileLabelStyle}>MAC</div>}
                      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {camera.mac_address || "—"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CameraHeartbeatWidget;
